import type { Database } from './supabase';

type Profile = Database['public']['Tables']['profiles']['Row'];

export interface ProfileCompletionStatus {
  percentage: number;
  missingFields: string[];
  completedFields: string[];
  isComplete: boolean;
}

/**
 * Calculate profile completion percentage for job seekers
 */
export function calculateProfileCompletion(profile: Profile): ProfileCompletionStatus {
  const requiredFields = [
    { key: 'full_name', label: 'Full Name', value: profile.full_name },
    { key: 'email', label: 'Email', value: profile.email },
    { key: 'location', label: 'Location', value: profile.location },
    { key: 'job_title', label: 'Desired Job Title', value: (profile as any).job_title },
    { key: 'experience_level', label: 'Experience Level', value: (profile as any).experience_level },
    { key: 'skills', label: 'Skills', value: (profile as any).skills?.length > 0 },
    { key: 'bio', label: 'About Me', value: profile.bio },
  ];

  const optionalFields = [
    { key: 'phone', label: 'Phone Number', value: (profile as any).phone },
    { key: 'avatar_url', label: 'Profile Photo', value: profile.avatar_url },
    { key: 'desired_job_types', label: 'Job Type Preferences', value: (profile as any).desired_job_types?.length > 0 },
    { key: 'min_salary', label: 'Salary Range', value: (profile as any).min_salary && (profile as any).max_salary },
  ];

  const completedFields: string[] = [];
  const missingFields: string[] = [];

  // Check required fields (70% weight)
  requiredFields.forEach((field) => {
    if (field.value) {
      completedFields.push(field.label);
    } else {
      missingFields.push(field.label);
    }
  });

  // Check optional fields (30% weight)
  const optionalCompleted: string[] = [];
  const optionalMissing: string[] = [];

  optionalFields.forEach((field) => {
    if (field.value) {
      optionalCompleted.push(field.label);
      completedFields.push(field.label);
    } else {
      optionalMissing.push(field.label);
      missingFields.push(field.label);
    }
  });

  // Calculate percentage
  const requiredScore = (requiredFields.filter(f => f.value).length / requiredFields.length) * 70;
  const optionalScore = (optionalCompleted.length / optionalFields.length) * 30;
  const percentage = Math.round(requiredScore + optionalScore);

  // Profile is considered complete if all required fields are filled and at least 50% of optional
  const isComplete =
    requiredFields.every(f => f.value) &&
    (optionalCompleted.length / optionalFields.length) >= 0.5;

  return {
    percentage,
    missingFields,
    completedFields,
    isComplete,
  };
}

/**
 * Get a color class for the completion percentage
 */
export function getCompletionColor(percentage: number): string {
  if (percentage >= 90) return 'text-green-600 bg-green-50';
  if (percentage >= 70) return 'text-orange-600 bg-orange-50';
  return 'text-red-600 bg-red-50';
}

/**
 * Get a message encouraging profile completion
 */
export function getCompletionMessage(percentage: number): string {
  if (percentage >= 90) return 'Your profile looks great!';
  if (percentage >= 70) return 'Almost there! Complete your profile for better matches';
  if (percentage >= 50) return 'Complete your profile to unlock better job matches';
  return 'Complete your profile to start finding great jobs';
}
